// Multi-organisation context (issue #194).
//
// Tracks the caller's full org membership list and which one is the
// "active" org. Active is persisted in localStorage so the dashboard
// remembers the user's last choice across reloads; the backend's
// `default_organization_id` is used the first time only.
//
// Consumers:
//   - Navbar org switcher (read+set).
//   - Dashboard, CreateEvent, EventOverview, etc. (read).
//
// `loading` is true while the first `org-switch-current` call is in flight.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "./useAuth";
import { functions as backendFunctions } from "../lib/backend/functions";

const STORAGE_KEY = "pikra_active_org_id";

const OrganizationContext = createContext(null);

function readStoredOrgId() {
  if (typeof window === "undefined" || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return typeof raw === "string" && raw.length > 0 ? raw : null;
  } catch {
    return null;
  }
}

function writeStoredOrgId(orgId) {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    if (orgId) window.localStorage.setItem(STORAGE_KEY, orgId);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage may be disabled (private mode / quota); fall through silently.
  }
}

export function OrganizationProvider({ children }) {
  const { user } = useAuth();
  const [memberships, setMemberships] = useState([]);
  const [activeOrgId, setActiveOrgIdState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!user) {
      setMemberships([]);
      setActiveOrgIdState(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: rpcError } = await backendFunctions
        .orgSwitchCurrent();
      if (rpcError) {
        setError(rpcError);
        setMemberships([]);
        setActiveOrgIdState(null);
        return;
      }
      const incoming = Array.isArray(data?.memberships) ? data.memberships : [];
      setMemberships(incoming);

      // Pick the active org: prefer the persisted choice if it's still
      // a valid membership, otherwise fall back to the server-suggested
      // default (owner-org first).
      const stored = readStoredOrgId();
      const storedIsValid = stored &&
        incoming.some((m) => m.organization_id === stored);
      const next = storedIsValid
        ? stored
        : (data?.default_organization_id ?? null);
      setActiveOrgIdState(next);
      writeStoredOrgId(next);
    } catch (err) {
      setError(err);
      setMemberships([]);
      setActiveOrgIdState(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const setActiveOrgId = useCallback(
    (orgId) => {
      const isValid = memberships.some((m) => m.organization_id === orgId);
      const next = isValid ? orgId : null;
      setActiveOrgIdState(next);
      writeStoredOrgId(next);
    },
    [memberships],
  );

  const value = useMemo(() => {
    const activeMembership = memberships.find(
      (m) => m.organization_id === activeOrgId,
    ) ?? null;
    return {
      memberships,
      activeOrganizationId: activeOrgId,
      activeOrganization: activeMembership?.organization ?? null,
      activeRole: activeMembership?.role ?? null,
      loading,
      error,
      setActiveOrganizationId: setActiveOrgId,
      refresh,
    };
  }, [memberships, activeOrgId, loading, error, setActiveOrgId, refresh]);

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const ctx = useContext(OrganizationContext);
  if (!ctx) {
    throw new Error(
      "useOrganization must be used within an OrganizationProvider",
    );
  }
  return ctx;
}
